/**
 * Created by Amir Absaliamov on 01.09.2026
 *
 * "Like!" pop-up shown after successful manual moves.
 *
 * Wiring per game:
 *   - scene component onReady():   cleverapps.likePraise.reset(adapter)
 *   - on a successful manual move: cleverapps.likePraise.notify(target, value, f)
 *     value is the move's worth, LikePraise.MOVE_VALUE for a plain move
 *   - on a wrong manual move:      cleverapps.likePraise.missed()
 *
 * The adapter decides what a "target" is and where its pop-up belongs on screen,
 * so the cadence below stays game-agnostic - see MistakesLikePraiseAdapter (arrows,
 * tapaway) and TileLikePraiseAdapter (jigsol).
 *
 * Loaded only by the feature "likepraise" in project.json. The bundle art is required,
 * so a theme without it fails the pack instead of silently losing the pop-up.
 */

class LikePraise {
    constructor() {
        this.adapter = undefined;
        this.showingLike = false;
        this.animations = [];
        this.lastAnimation = undefined;
        this.shuffleThresholds();
        this.resetCadence();
    }

    // Called by a game's scene component when a level is ready.
    reset(adapter) {
        this.adapter = adapter;
        this.showingLike = false;
        this.animations = [];
        this.lastAnimation = undefined;
        this.shuffleThresholds();
        this.resetCadence();
    }

    shuffleThresholds() {
        this.thresholds = PseudoRandom.GET("likepraise").shuffle(LikePraise.THRESHOLDS);
        this.index = 0;
    }

    resetCadence() {
        this.weight = 0;
        this.threshold = this.thresholds[this.index];
        this.index = (this.index + 1) % this.thresholds.length;
    }

    // "value" is how much this move is worth. Games that can tell a big move from
    // a small one pass more so it earns praise sooner (jigsol counts a multi-tile
    // snap double); games where every move is alike pass the default MOVE_VALUE.
    wantsLike(value) {
        this.weight += value;

        if (this.weight < this.threshold) {
            return false;
        }

        this.resetCadence();
        return true;
    }

    missed() {
        this.weight = 0;
    }

    forcedByTutorial() {
        const tutorial = this.adapter && this.adapter.tutorial;

        return Boolean(tutorial) && cleverapps.tutorial.checkConditions({ tutorial });
    }

    // f always fires, pop-up or not: callers chain it into a waiter that gates the
    // move's counter, so swallowing it on a skipped praise would stall the game.
    notify(target, value = LikePraise.MOVE_VALUE, f = () => {}) {
        const worldPos = target && !this.showingLike && this.adapter && this.adapter.getWorldPosition(target);

        if (worldPos && (this.forcedByTutorial() || this.wantsLike(value))) {
            this.showLike(worldPos, f);
        } else {
            f();
        }
    }

    // Shuffle bag over whatever animation variants the spine ships, avoiding an
    // immediate repeat across bags.
    nextAnimation(available) {
        if (!this.animations.length) {
            this.animations = PseudoRandom.GET("likepraise").shuffle(available);

            if (available.length > 1 && this.animations[this.animations.length - 1] === this.lastAnimation) {
                this.animations.unshift(this.animations.pop());
            }
        }

        this.lastAnimation = this.animations.pop();
        return this.lastAnimation;
    }

    clampToScene(popup, movingNode) {
        const scene = cleverapps.Scenes.GetRunningScene();
        const pad = LikePraise.SCREEN_PADDING;

        const min = movingNode.convertToNodeSpace(scene.convertToWorldSpace(cc.p(pad, pad)));
        const max = movingNode.convertToNodeSpace(scene.convertToWorldSpace(cc.p(scene.width - pad, scene.height - pad)));

        const box = popup.getBoundingBoxToWorld();
        const bottomLeft = movingNode.convertToNodeSpace(cc.p(box.x, box.y));
        const topRight = movingNode.convertToNodeSpace(cc.p(box.x + box.width, box.y + box.height));

        let dx = 0;
        let dy = 0;

        if (bottomLeft.x < min.x) {
            dx = min.x - bottomLeft.x;
        } else if (topRight.x > max.x) {
            dx = max.x - topRight.x;
        }

        if (bottomLeft.y < min.y) {
            dy = min.y - bottomLeft.y;
        } else if (topRight.y > max.y) {
            dy = max.y - topRight.y;
        }

        if (dx || dy) {
            popup.setPosition(popup.x + dx, popup.y + dy);
        }
    }

    showLike(worldPos, f) {
        this.showingLike = true;

        const movingNode = cleverapps.Scenes.GetMovingNode();
        const like = new cleverapps.Spine(bundles.likepraise.jsons.like_praise_json);
        movingNode.addChild(like, 10000);

        if (this.adapter.anchor) {
            like.setAnchorPoint(this.adapter.anchor);
        }

        like.setPosition(movingNode.convertToNodeSpace(worldPos));
        this.clampToScene(like, movingNode);

        like.setAnimation(0, this.nextAnimation(like.listAnimations()), false);
        like.setCompleteListener(() => {
            this.showingLike = false;
            like.removeFromParent();
            f();
        });

        cleverapps.effects.fire("likepraise.like");
    }
}

// Thresholds and move values share one scale: a plain move is worth MOVE_VALUE, so the
// bag asks for 2-5 plain moves and leaves room for in-between values (see ArrowMoveQuality).
LikePraise.THRESHOLDS = [20, 30, 40, 50];
LikePraise.MOVE_VALUE = 10;
LikePraise.SCREEN_PADDING = 20;

cleverapps.likePraise = new LikePraise();
